import { fetchProjectById, Project } from '../../../store/Project';
import { TextSpan } from '../../Atoms/Text';
import { formatDate } from '../../../utils/helpers';
import { Tags } from '../../Compounds/Tagging';
import { SupportedTagEntityTypes } from '../../../store/Tags';
import { GroupedGridColDef } from '../../Compounds/UpstackDataGrid/gridColumnTypes';
import { projectConfigDetailColumns } from './projectGridColumnsDefs';
import { isCardFieldVisible, useAccountGridConfig, useColumnsTurnedOn } from '../../../store/AccountCustomization';
import { ProjectCustomFieldsSection } from './ProjectCustomFieldsSection';

export interface ProjectDetailsSectionProps {
  project: Project;
}

type ProjectValueGetter = (value: unknown, row: Project) => unknown;

const DETAIL_FIELD_LAYOUT: Record<
  string,
  { size?: 'sm' | 'md' | 'lg'; preserveWhitespace?: boolean; className: string }
> = {
  tags: { size: 'md', className: 'mb-4 pr-2 col-span-3 w-full' },
  nextSteps: { size: 'lg', preserveWhitespace: true, className: 'mb-4 pr-2 col-span-6 w-full' }
};
const DEFAULT_FIELD_LAYOUT = { size: 'sm' as const, className: 'mb-4 pr-2 col-span-3 w-full' };

// Every valueGetter in projectGridColumnsDefs.tsx only reads its row (2nd) argument, so it can be
// called directly outside a MUI grid context. projectStartDate uses a valueFormatter instead
// (which expects an already-resolved cell value, not a row) and keeps its own formatting here.
const getFieldValue = (col: GroupedGridColDef, project: Project): string => {
  if (col.field === 'projectStartDate') {
    return formatDate(project.projectStartDate);
  }
  if (col.valueGetter) {
    const value = (col.valueGetter as unknown as ProjectValueGetter)(undefined, project);
    return value == null ? '' : String(value);
  }
  return ((project as unknown as Record<string, string>)[col.field] as string) ?? '';
};

export const ProjectDetailsSection = ({ project }: ProjectDetailsSectionProps) => {
  // The multi-asset card mirrors the Projects table: the account's columns, plus any the user turned on there.
  const accountGridConfig = useAccountGridConfig(project.account?.id, 'project_grid');
  const turnedOn = useColumnsTurnedOn('project_grid', accountGridConfig);
  const visibleFields = projectConfigDetailColumns().filter((col) =>
    isCardFieldVisible(col.field, col.defaultHidden, accountGridConfig, turnedOn)
  );

  return (
    <>
      <div className="grid grid-cols-6 gap-2 place-items-start">
        {visibleFields.map((col) => {
          const layout = DETAIL_FIELD_LAYOUT[col.field] || DEFAULT_FIELD_LAYOUT;
          const fieldName = col.headerName || col.field;

          if (col.field === 'tags') {
            return (
              <ProjectDetailField
                key={col.field}
                fieldName={fieldName}
                className={layout.className}
                size={layout.size}>
                <Tags
                  entity={project}
                  entityId={project.id}
                  entityAccountId={project.account?.id}
                  entityType={SupportedTagEntityTypes.PROJECT}
                  entityFetchFn={(id) => fetchProjectById(id)}
                  showCTAButton={true}
                />
              </ProjectDetailField>
            );
          }

          const value = getFieldValue(col, project);

          return (
            <ProjectDetailField
              key={col.field}
              fieldName={fieldName}
              value={value}
              className={layout.className}
              size={layout.size}
              preserveWhitespace={layout.preserveWhitespace}
            />
          );
        })}
      </div>
      <ProjectCustomFieldsSection
        project={project}
        className="mt-4 pt-4 border-t border-gray-200"
      />
    </>
  );
};

const ProjectDetailField = ({
  fieldName,
  value,
  className,
  size = 'sm',
  preserveWhitespace = false,
  children
}: {
  fieldName: string;
  value?: string;
  className: string;
  size?: 'sm' | 'md' | 'lg';
  preserveWhitespace?: boolean;
  children?: React.ReactNode;
}) => {
  const SMALL_SIZE_WRAPPER = 'grid-cols-2';
  const MEDIUM_SIZE_WRAPPER = 'grid-cols-3';
  const LARGE_SIZE_WRAPPER = 'grid-cols-6';

  const SMALL_LABEL = 'col-span-1';
  const MEDIUM_LABEL = 'col-span-1';
  const LARGE_LABEL = 'col-span-1';

  const SMALL_VALUE = 'col-span-1';
  const MEDIUM_VALUE = 'col-span-2';
  const LARGE_VALUE = 'col-span-5';

  const getWrapperSize = () => {
    switch (size) {
      case 'md':
        return MEDIUM_SIZE_WRAPPER;
      case 'lg':
        return LARGE_SIZE_WRAPPER;
      case 'sm':
      default:
        return SMALL_SIZE_WRAPPER;
    }
  };

  const getLabelSize = () => {
    switch (size) {
      case 'md':
        return MEDIUM_LABEL;
      case 'lg':
        return LARGE_LABEL;
      case 'sm':
      default:
        return SMALL_LABEL;
    }
  };

  const getValueSize = () => {
    switch (size) {
      case 'md':
        return MEDIUM_VALUE;
      case 'lg':
        return LARGE_VALUE;
      case 'sm':
      default:
        return SMALL_VALUE;
    }
  };
  return (
    <div className={className}>
      <div className={`grid ${getWrapperSize()} gap-2 place-content-stretch w-full`}>
        <div className={getLabelSize()}>
          <TextSpan
            weight="semibold"
            size="sm14">
            {fieldName}
          </TextSpan>
        </div>
        <div className={`${getValueSize()}${preserveWhitespace ? ' whitespace-pre-wrap' : ''}`}>
          <TextSpan
            weight="normal"
            size="sm">
            {value || children || ''}
          </TextSpan>
        </div>
      </div>
    </div>
  );
};
